import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CustomRegistrationForm = () => {
  const navigate = useNavigate();
  const [formReady, setFormReady] = useState(false);
  const [forceEnabled, setForceEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const validateField = (name: string, value: string) => {
    if (name === 'name' && (!value || value.trim() === '')) {
      return 'Por favor, insira seu nome';
    }
    if (name === 'email') {
      if (!value || value.trim() === '') {
        return 'Por favor, insira seu email';
      }
      if (!value.includes('@')) {
        return 'Por favor, insira um email válido';
      }
    }
    if (name === 'phone' && (!value || value.trim() === '')) {
      return 'Por favor, insira seu telefone';
    }
    return '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: errorMessage }));
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://umcanaldeluz91215.activehosted.com/f/embed.php?id=217';
    script.async = true;
    
    script.onload = () => {
      console.log('ActiveCampaign script loaded');
      
      let attempts = 0;
      const maxAttempts = 10;
      
      const checkFormReady = setInterval(() => {
        attempts++;
        
        const formExists = document.querySelector('#active-campaign-container form._form');
        
        if (window._form_217_submit || formExists) {
          console.log('ActiveCampaign form is ready');
          setFormReady(true);
          clearInterval(checkFormReady);
        } else if (attempts >= maxAttempts) {
          console.log('Failed to load ActiveCampaign form after multiple attempts');
          clearInterval(checkFormReady);
          
          setTimeout(() => {
            setForceEnabled(true);
            console.log('Form button force enabled');
          }, 1000);
        }
      }, 500);
    };
    
    document.body.appendChild(script);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const forms = document.querySelectorAll('form._form');
          forms.forEach(form => {
            const container = document.getElementById('active-campaign-container');
            if (form.parentElement !== container && !container?.contains(form)) {
              form.remove();
            }
          });
          
          const formInContainer = document.querySelector('#active-campaign-container form._form');
          if (formInContainer && !formReady) {
            console.log('Form detected in container');
            setFormReady(true);
          }
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleActiveSubmit = (event: Event) => {
      console.log('Form submitted!');
      toast.success('Registro realizado com sucesso!');
      setIsSubmitting(false);
      
      setTimeout(() => {
        navigate('/registration-success');
      }, 1500);
    };

    window.addEventListener('_form_callback', handleActiveSubmit);

    const globalTimeout = setTimeout(() => {
      if (!formReady) {
        setForceEnabled(true);
        console.log('Global timeout reached - force enabling form');
      }
    }, 8000);

    return () => {
      document.body.removeChild(script);
      window.removeEventListener('_form_callback', handleActiveSubmit);
      observer.disconnect();
      clearTimeout(globalTimeout);
    };
  }, [formReady, navigate]);

  const submitDirectToActiveCampaign = async (name: string, email: string, phone: string) => {
    try {
      console.log('Submitting directly to ActiveCampaign API');
      
      const formDataToSend = new FormData();
      formDataToSend.append('u', '217');
      formDataToSend.append('f', '217');
      formDataToSend.append('s', '');
      formDataToSend.append('c', '0');
      formDataToSend.append('m', '0');
      formDataToSend.append('act', 'sub');
      formDataToSend.append('v', '2');
      formDataToSend.append('or', 'ca9c3b14eb2e9ebbfb8d8c632a6bad16');
      
      formDataToSend.append('firstname', name);
      formDataToSend.append('email', email);
      formDataToSend.append('phone', phone);
      
      formDataToSend.append('tags', 'ANA25 - Lead Inscrito');
      
      const response = await fetch('https://umcanaldeluz91215.activehosted.com/proc.php', {
        method: 'POST',
        body: formDataToSend,
        mode: 'no-cors'
      });
      
      console.log('Direct submission completed');
      return true;
    } catch (error) {
      console.error('Error in direct submission:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setHasSubmitted(true);
    
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    
    const nameError = validateField('name', name);
    const emailError = validateField('email', email);
    const phoneError = validateField('phone', phone);
    
    setErrors({
      name: nameError,
      email: emailError,
      phone: phoneError
    });
    
    if (nameError || emailError || phoneError) {
      if (nameError) toast.error(nameError);
      else if (emailError) toast.error(emailError);
      else if (phoneError) toast.error(phoneError);
      
      setIsSubmitting(false);
      return;
    }
    
    let submitted = false;
    
    if (window._form_217_submit) {
      try {
        console.log('Attempting to use ActiveCampaign embedded form submission');
        const hiddenForm = document.getElementById('_form_217_') as HTMLFormElement;
        if (hiddenForm) {
          const firstnameInput = hiddenForm.querySelector('[name="firstname"]') as HTMLInputElement;
          const emailInput = hiddenForm.querySelector('[name="email"]') as HTMLInputElement;
          const phoneInput = hiddenForm.querySelector('[name="phone"]') as HTMLInputElement;
          
          let tagInput = hiddenForm.querySelector('[name="tags"]') as HTMLInputElement;
          if (!tagInput) {
            tagInput = document.createElement('input');
            tagInput.type = 'hidden';
            tagInput.name = 'tags';
            hiddenForm.appendChild(tagInput);
          }
          
          if (firstnameInput) firstnameInput.value = name;
          if (emailInput) emailInput.value = email;
          if (phoneInput) phoneInput.value = phone;
          if (tagInput) tagInput.value = 'ANA25 - Lead Inscrito';
          
          console.log('Submitting ActiveCampaign form with tag: ANA25 - Lead Inscrito');
          window._form_217_submit();
          submitted = true;
        } else {
          console.error('Hidden form not found - trying direct API submission');
          submitted = await submitDirectToActiveCampaign(name, email, phone);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        submitted = await submitDirectToActiveCampaign(name, email, phone);
      }
    } else {
      console.error('_form_217_submit function not available - trying direct API submission');
      submitted = await submitDirectToActiveCampaign(name, email, phone);
    }
    
    if (!submitted) {
      console.log('All submission methods failed, using fallback');
      toast.success('Registro realizado com sucesso!');
      
      setTimeout(() => {
        navigate('/registration-success');
      }, 1500);
    } else {
      setTimeout(() => {
        navigate('/registration-success');
      }, 1500);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white rounded-lg shadow-sm">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Nome <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
            className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
            placeholder="Escreva seu nome"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>
        
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            E-mail <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
            className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
            placeholder="Digite seu melhor e-mail"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
        
        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Telefone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
            className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
            placeholder="DDI + DDD + Telefone"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>
        
        <Button 
          type="submit"
          className="w-full py-3 px-4 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] text-white font-semibold rounded-md shadow-sm hover:translate-y-[-2px] transition-all duration-200 hover:shadow-md"
          disabled={(!formReady && !forceEnabled) || isSubmitting}
        >
          {isSubmitting ? 'ENVIANDO...' : hasSubmitted ? 'AGUARDE...' : (formReady || forceEnabled ? 'QUERO PARTICIPAR' : 'CARREGANDO...')}
        </Button>
        
        {!formReady && !forceEnabled && (
          <p className="text-xs text-center text-gray-500">
            Aguarde enquanto preparamos seu formulário de inscrição...
          </p>
        )}
      </form>
      
      <div style={{ display: 'none' }} id="active-campaign-container"></div>
    </div>
  );
};

declare global {
  interface Window {
    _form_217_submit: () => void;
    _form_callback: (id: string) => void;
  }
}

export default CustomRegistrationForm;
